import axios, { AxiosResponse } from 'axios';
import { mutate } from 'swr';
import { getError } from '@shared/utils/helpers';
import { IDealSheet } from '@modules/shared/DealSheet/interfaces';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { IError } from '@shared/interfaces';
import { SERVICES } from '@shared/constants/apiConfig';
import { 
	IContactsRequestBody, 
	IDeal, 
	IDealCalculate, 
	IDealComment, 
	IDealFee, 
	IDealUser, 
	IDelays, 
	IOpenLendingMisMatch, 
	IPushAppDTData, 
	IPushAppData, 
	IPushSubprimeLenderData, 
	ISubprimeResponse, 
	ITitleDocuments,
} from './interfaces';
import { IAutoLoans } from './components/AutoLoans/interfaces';

const path = `/${SERVICES.DEAL}`;
const accountPath = `/${SERVICES.ACCOUNTING}`;

export const requestCopyDeal = (dealId: string, contactId: string) => {
  return mutate(`${path}/contacts/${contactId}/deals/summary`, async () => {
    try {
      const resp = await axios.post(`${path}/deals/${dealId}/copy`);
      Notistack.successMessage('Deal - Copied Successfully');
      return resp?.data;
    } catch (err) {
      const error = err as { response: { data: { message: string } } };
      Notistack.generalError(error?.response?.data?.message);
    }
  });
};

export const requestUpdateDeal = (deal: IDeal, _contactId?: string) => {
  const {
    id: dealId,
    leadId,
    lenderResponse: { lenderCode },
    isPicked,
    isDocsGenerated,
    isHotTitles,
    isBuyBack,
    downPaymentStatus,
    isBlueAdvantage,
    isGenysisWithHoldings,
    financeConditionItem,
    carrierTrackingNumberIn,
    carrierTrackingNumberOut,
    isAch,
    isGrossUpIncome,
    cudlAccountNumber,
    postLoanDti,
    isRecalculatingTitleAndDocFees = false,
  } = deal;

  const request = {
    dealId,
    leadId,
    lenderCode,
    isPicked,
    isDocsGenerated,
    isHotTitles,
    isBuyBack,
    downPaymentStatus,
    isBlueAdvantage,
    isGenysisWithHoldings,
    financeConditionItemResponse: financeConditionItem,
    carrierTrackingNumberIn,
    carrierTrackingNumberOut,
    isAch,
    isGrossUpIncome,
    loanApplicationNumber: cudlAccountNumber,
    postLoanDti,
    isRecalculatingTitleAndDocFees,
  };
  const url = `${path}/deals/${request.dealId}`;
  return mutate(url, async () => {
    try {
      const result = await axios.put(`${path}/deals/${request.dealId}`, request);
      Notistack.successMessage('Deal - Updated Successfully');
      return result;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDeleteDeal = (dealId: string) => {
  return mutate(path, async () => {
    try {
      await axios.delete(`${path}/deals/${dealId}`);
      Notistack.successMessage('Deal - Deleted Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestConditionCalculate = (data: IDealCalculate) => {
  const url = `${path}/deals/${data.dealId}/calculate-and-update`;
  return mutate(url, async () => {
    try {
      const response = await axios.post(url, data);
      Notistack.successMessage('Calculations done successfully');
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDealToggle = (dealId: string) => {
  const url = `${path}/deals/${dealId}/active`;
  return mutate(url, async () => {
    try {
      await axios.post(url, {});
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const handleEditDealUser = (dealId: string, data: IDealUser[]) => {
  const url = `${path}/deals/${dealId}/users`;

  return mutate(url, async () => {
    try {
      await axios.put(url, {
        dealUserAssignmentType: 1,
        dealUsers: data,
      });
      Notistack.successMessage('Deal users - changed successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const handleEditCoBorrower = (dealId: string, data: IContactsRequestBody) => {
  const url = `${path}/deals/${dealId}/co-contact`;
  return mutate(url, async () => {
    try {
      await axios.patch(url, data);
      Notistack.successMessage('Deals - changed borrower');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestPushApp = async (pushAppData: IPushAppData) => {
  const url = `/${SERVICES.LENDER}/loansubmission/submit-deal-to-lender`;
  try {
    const resp = await axios.post(url, pushAppData);
    if (!resp.data?.errors?.length) {
      Notistack.successMessage('App Submitted Successfully');
    }

    return resp.data;
  } catch (err) {
    const error = err as IError;
    if (error.response.status === 500) {
      Notistack.generalError(getError(err));
    }

    return err;
  }
};

export const requestSaveDelay = (data: IDelays) => {
  const url = `${path}/deals/delay`;
  return mutate(url, async () => {
    try {
      await axios.post(url, data);
      Notistack.successMessage('Successfully Saved');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDeleteDealSheet = (data: IDealSheet) => {
  const url = `/${SERVICES.ACCOUNTING}/accounting/${data.id}`;
  return mutate(url, async () => {
    try {
      await axios.delete(url);
      Notistack.successMessage('Dealsheet -Successfully Deleted');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestSaveTitleDocGen = (data: ITitleDocuments) => {
  const url = `${SERVICES.DOCUMENT}/documents/titledocgenindex`;
  return mutate(url, async () => {
    try {
      await axios.post(url, data);
      Notistack.successMessage('Successfully Saved');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestHotTitle = async (dealId: string, hotTitle = false) => {
  const url = `${path}/deals/${dealId}/hot-title/${hotTitle}`;
  try {
    await axios.put(url);
    Notistack.successMessage('Deal - Updated Successfully');
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestBuyBack = async (dealId: string, buyBack = false) => {
  const url = `${path}/deals/${dealId}/buy-back/${buyBack}`;
  try {
    await axios.put(url);
    Notistack.successMessage('Deal - Updated Successfully');
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

/** Doc Fees requests */
export const requestDealFees = (dealId: string) => {
  const url = `${path}/deals/${dealId}/dealfees`;
  return mutate(url, async () => {
    try {
      const response = await axios.get(url);
      return response?.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDealCreateDocFee = (dealId: string, data?: IDealFee) => {
  const url = `${path}/deals/${dealId}/dealfees`;
  return mutate(url, async () => {
    try {
      const response = await axios.post(url, data);
      Notistack.successMessage('Deal fee created successfully');
      return response;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDealUpdateDocFee = (dealId: string, data: IDealFee) => {
  const url = `${path}/deals/${dealId}/dealfees`;
  return mutate(url, async () => {
    try {
      const response = await axios.put(url, data);
      Notistack.successMessage('Deal fee updated successfully');
      return response;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDealDeleteDocFee = (dealId: string, dealFeeId: string) => {
  const url = `${path}/deals/${dealId}/dealfees/${dealFeeId}`;
  return mutate(url, async () => {
    try {
      const response = await axios.delete(url);
      Notistack.successMessage('Deal fee deleted successfully');
      return response;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestGetCreditReports = async (contactId: string) => {
  const url = `/${SERVICES.EXTERNAL}/credit-reports/${contactId}/auto-loan?bureauType=2`;
  try {
    const response: AxiosResponse<IAutoLoans[]> = await axios.get(url);
    return response?.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestPushToOpenLending = async (pushAppData: IPushAppData) => {
  const url = `/${SERVICES.LENDER}/open-lending/loan-application`;
  try {
    const resp = await axios.post(url, pushAppData);
    if (!resp.data.statusReasons.length || resp.data.applicationStatus === 'Approved') {
      Notistack.successMessage('App Submitted Successfully');
    } else {
      const errors = resp.data.statusReasons.reduce((acc: string, item: string) => (acc += item + '\n'));
      Notistack.generalError(errors);
    }
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestPushAppToDT = async (pushAppData: IPushAppDTData) => {
  const url = `/${SERVICES.LENDER}/dealertrack/loan-application/submit`;
  try {
    const response = await axios.post(url, pushAppData);
    return response?.data;
  } catch (err) {
    const error = err as IError;
    if (error.response.status === 500) {
      Notistack.generalError(getError(err));
    }

    return err;
  }
};

export const requestCreateSubprimeDeal = async (contactId: string, leadId: string, dealId: string) => {
  const url = `/${SERVICES.DEAL}/contacts/${contactId}/leads/${leadId}/deals/${dealId}/create`;
  try {
    const response: AxiosResponse<ISubprimeResponse[]> = await axios.post(url);
    return response.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestPushSubprimeLenders = async (pushData: IPushSubprimeLenderData[]) => {
  const url = `/${SERVICES.LENDER}/subprime/submit`;
  try {
    const response: AxiosResponse<ISubprimeResponse[]> = await axios.post(url, pushData);
    return response.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestFlexCertificateMisMatch = async (applicationId: string, dealId: string) => {
  const path = `/${SERVICES.LENDER}/open-lending/${dealId}/loan-application/${applicationId}/certification/data-check`;
  const dataMismatchObj = {
    isDataMismatchCheck: false,
    listOfPropertiesToCheck: [],
  };
  try {
    const response = await axios.post<IOpenLendingMisMatch>(path, dataMismatchObj);
    return response.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const addDealComment = async (dealId: string, data: IDealComment) => {
  const url = `/${SERVICES.DEAL}/deals/${dealId}/comments`;
  try {
    return axios.post(url, data);
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const viewDealSummary = async (dealId: string) => {
  const url = `${accountPath}/accounting/deal/summary/${dealId}`;
  return mutate(url, async () => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};